import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { stages } from '../data/stages';
import { levels } from '../data/levels';
import { LevelStatus } from '../types';
import StarRating from '../components/StarRating';
import ProgressRing from '../components/ProgressRing';
import styles from './StageMap.module.css';

const StageMap: React.FC = () => {
  const navigate = useNavigate();
  const completedLevels = useAppStore((s) => s.completedLevels);
  const babyStage = useAppStore((s) => s.currentStage);
  const stageRate = useAppStore((s) => s.getStageCompletionRate());
  const totalStars = useAppStore((s) => s.getTotalStars());
  const currentTitle = useAppStore((s) => s.getCurrentTitle());
  const getStarsForLevel = useAppStore((s) => s.getStarsForLevel);
  const streak = useAppStore((s) => s.streak);
  const dailyFirstWinDate = useAppStore((s) => s.dailyFirstWinDate);
  const stageCompleted = useAppStore((s) => s.badgeAuxData.stageCompleted);
  const manuallyUnlockedStages = useAppStore((s) => s.manuallyUnlockedStages);
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [tooltipStage, setTooltipStage] = useState<string | null>(null);

  // 阶段解锁：双重条件 — 宝宝月龄保底 + 闯关完成可提前解锁下一阶段
  // 闯关解锁需满足：前一阶段所有关卡已完成 且 每关至少获得1星（正确率>=50%）
  const stageUnlockStatus = React.useMemo(() => {
    const status: Record<string, boolean> = {};
    const completedPerStage: Record<string, number> = {};
    const starsPerStage: Record<string, number> = {};
    const levelsWithStarPerStage: Record<string, number> = {};
    const totalPerStage: Record<string, number> = {};
    const uniqueCompleted = new Set(completedLevels.map((cl) => cl.levelId));

    // 统计每个阶段的星数（取每个关卡最新一次完成记录）
    const levelBest: Record<string, { correctCount: number; totalCount: number; timestamp: number }> = {};
    completedLevels.forEach((cl) => {
      if (!levelBest[cl.levelId] || cl.timestamp > levelBest[cl.levelId].timestamp) {
        levelBest[cl.levelId] = { correctCount: cl.correctCount, totalCount: cl.totalCount, timestamp: cl.timestamp };
      }
    });

    uniqueCompleted.forEach((levelId) => {
      const level = levels.find((l) => l.id === levelId);
      if (level) {
        completedPerStage[level.stage] = (completedPerStage[level.stage] || 0) + 1;
        const best = levelBest[levelId];
        if (best && best.totalCount > 0) {
          const rate = best.correctCount / best.totalCount;
          const stars = rate >= 0.9 ? 3 : rate >= 0.7 ? 2 : rate >= 0.5 ? 1 : 0;
          starsPerStage[level.stage] = (starsPerStage[level.stage] || 0) + stars;
          if (stars >= 1) {
            levelsWithStarPerStage[level.stage] = (levelsWithStarPerStage[level.stage] || 0) + 1;
          }
        }
      }
    });
    levels.forEach((l) => {
      totalPerStage[l.stage] = (totalPerStage[l.stage] || 0) + 1;
    });

    const babyStageIndex = stages.findIndex((s) => s.name === babyStage);
    const ageUnlockUpTo = babyStageIndex >= 0 ? babyStageIndex : 0;

    stages.forEach((s, i) => {
      if (i <= ageUnlockUpTo || manuallyUnlockedStages.includes(s.name)) {
        status[s.name] = true;
      } else {
        const prevStageName = stages[i - 1].name;
        const prevTotal = totalPerStage[prevStageName] || 1;
        const prevCompleted = completedPerStage[prevStageName] || 0;
        const prevLevelsWithStar = levelsWithStarPerStage[prevStageName] || 0;
        // 前一阶段全部关卡完成，且每关至少获得1星（正确率>=50%）
        status[s.name] = prevCompleted >= prevTotal && prevLevelsWithStar >= prevTotal;
      }
    });
    return status;
  }, [babyStage, completedLevels, manuallyUnlockedStages]);

  const [activeStage, setActiveStage] = useState(stages[0]);
  // 当前激活的阶段（默认第一个解锁的阶段）
  const currentStage = stageUnlockStatus[activeStage.name] ? activeStage : stages.find((s) => stageUnlockStatus[s.name]) || stages[0];

  const today = new Date().toISOString().slice(0, 10);
  const hasStudiedToday = streak.lastStudyDate === today;

  const statusIcon = (status: LevelStatus): string => {
    switch (status) {
      case LevelStatus.COMPLETED:
        return '✔';
      case LevelStatus.IN_PROGRESS:
        return '●';
      case LevelStatus.NEED_REVIEW:
        return '⚠';
      case LevelStatus.LOCKED:
        return '🔒';
      case LevelStatus.NOT_STARTED:
      default:
        return '○';
    }
  };

  const statusClass = (status: LevelStatus): string => {
    switch (status) {
      case LevelStatus.COMPLETED:
        return styles.completed;
      case LevelStatus.IN_PROGRESS:
        return styles.inProgress;
      case LevelStatus.NEED_REVIEW:
        return styles.needReview;
      case LevelStatus.LOCKED:
        return styles.locked;
      case LevelStatus.NOT_STARTED:
      default:
        return styles.notStarted;
    }
  };

  const getModuleLevels = (stageId: string, moduleName: string) => {
    const stageName = stages.find((s) => s.id === stageId)?.name;
    return levels.filter((l) => l.stage === stageName && l.module === moduleName);
  };

  const getModuleCompletedCount = (moduleName: string) => {
    const moduleLevels = getModuleLevels(currentStage.id, moduleName);
    return moduleLevels.filter((l) =>
      completedLevels.some((cl) => cl.levelId === l.id)
    ).length;
  };

  const getModuleStatus = (moduleName: string): LevelStatus => {
    const moduleLevels = getModuleLevels(currentStage.id, moduleName);
    const completed = getModuleCompletedCount(moduleName);
    if (completed === moduleLevels.length && moduleLevels.length > 0) return LevelStatus.COMPLETED;
    if (completed > 0) return LevelStatus.IN_PROGRESS;
    return LevelStatus.NOT_STARTED;
  };

  const getRecommendedLevel = () => {
    // 优先使用 store 中的每日推荐
    const dr = useAppStore.getState().dailyRecommend;
    if (dr && !dr.completed) {
      const rec = levels.find((l) => l.id === dr.recommendedLevelId);
      if (rec) return rec;
    }
    // 回退：在当前阶段找第一个未完成的关卡
    const currentLevels = levels.filter((l) => l.stage === currentStage.name);
    const next = currentLevels.find(
      (l) => !completedLevels.some((cl) => cl.levelId === l.id)
    );
    return next || currentLevels[0];
  };

  const recommended = getRecommendedLevel();

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.headerLeft}>
            <h1 className={styles.mainTitle}>我的 0-12 月育儿学习地图</h1>
            {streak.currentStreak > 0 && (
              <span className={styles.streakBadge}>
                🔥 连续 {streak.currentStreak} 天
              </span>
            )}
          </div>
          <ProgressRing
            percent={stageRate}
            size={72}
            strokeWidth={6}
            color="#e8876b"
            label={`${stageRate}%`}
            sublabel="完成率"
          />
        </div>
        <div className={styles.currentInfo}>
          <span className={styles.stageLabel}>当前阶段：{currentStage.name}</span>
          <span className={styles.titleBadge}>
            {currentTitle} · ⭐ {totalStars} 颗
          </span>
        </div>
        {/* 已完成阶段显示"阶段总结"入口 */}
        {stageCompleted.includes(currentStage.name) && (
          <button
            className={styles.stageSummaryBtn}
            onClick={() => navigate('/stage-complete', { state: { stage: currentStage.name } })}
          >
            📊 阶段总结
          </button>
        )}
      </header>

      {/* 推荐下一关 */}
      {recommended && (
        <section className={styles.recommendSection}>
          <h2 className={styles.recommendTitle}>推荐下一关</h2>
          <div className={styles.recommendCard}>
            <span className={styles.recommendModule}>[{recommended.module}]</span>
            <span className={styles.recommendName}>{recommended.title}</span>
            <div className={styles.recommendMeta}>
              预计 {recommended.estimatedTime} 分钟 · 情景练习
            </div>
            <button className={styles.continueButton} onClick={() => navigate(`/level/${recommended.id}`)}>继续学习</button>
          </div>
        </section>
      )}

      {/* 阶段混合闯关入口 */}
      {completedLevels.length >= 1 && (
        <section className={styles.challengeSection}>
          <div className={styles.challengeCard} onClick={() => navigate(`/stage-challenge/${encodeURIComponent(currentStage.name)}`)}>
            <div className={styles.challengeCardLeft}>
              <span className={styles.challengeIcon}>⚔️</span>
              <div>
                <div className={styles.challengeTitle}>阶段混合闯关</div>
                <div className={styles.challengeSub}>
                  跨模块综合挑战 · 检验你的掌握水平
                </div>
              </div>
            </div>
            <span className={styles.challengeArrow}>→</span>
          </div>
          {/* 连胜守护提醒 */}
          {streak.currentStreak >= 2 && !hasStudiedToday && (
            <div className={styles.streakWarning}>
              <span>⚠️</span>
              <span>今天不闯关，<strong>{streak.currentStreak}</strong> 天连胜将断！</span>
            </div>
          )}
          {dailyFirstWinDate !== today && (
            <div className={styles.firstWinHint}>
              <span>🎯</span>
              <span>今日首胜等你来拿！</span>
            </div>
          )}
        </section>
      )}

      {/* 阶段切换标签 */}
      <section className={styles.section}>
        <div className={styles.stageTabs}>
          {stages.map((stage) => {
            const unlocked = stageUnlockStatus[stage.name];
            const isActive = currentStage.name === stage.name;
            const isCompleted = stageCompleted.includes(stage.name);

            return (
              <span
                key={stage.id}
                className={`${styles.stageTabWrapper} ${!unlocked ? styles.stageTabWrapperLocked : ''}`}
                onMouseEnter={() => { if (!unlocked) setTooltipStage(stage.name); }}
                onMouseLeave={() => setTooltipStage(null)}
              >
                <button
                  className={`${styles.stageTab} ${isActive ? styles.stageTabActive : ''} ${!unlocked ? styles.stageTabLocked : ''}`}
                  onClick={() => unlocked && setActiveStage(stage)}
                  disabled={!unlocked}
                >
                  {!unlocked && <span className={styles.stageTabLock}>🔒 </span>}
                  <span className={styles.stageTabName}>{stage.name}</span>
                  {isCompleted && <span className={styles.stageCompletedBadge}>✓</span>}
                  {isActive && unlocked && <span className={styles.stageTabDot} />}
                </button>
              </span>
            );
          })}
        </div>

        {/* 解锁提示条：在 tabs 下方，普通文档流，推动内容下移 */}
        {(() => {
          const hoveredStage = tooltipStage
            ? stages.find((s) => s.name === tooltipStage)
            : null;
          if (!hoveredStage) return null;
          const prevIdx = stages.findIndex((s) => s.name === hoveredStage.name);
          const prevStage = stages[prevIdx - 1];
          if (!prevStage) return null;
          return (
            <div className={styles.lockInfoBar}>
              <div className={styles.lockInfoBarTitle}>🔒「{hoveredStage.name}」尚未解锁</div>
              <div className={styles.lockInfoBarWays}>
                <div className={styles.lockInfoWay}>
                  <span className={styles.lockInfoWayLabel}>方式一</span>
                  宝宝月龄到达「<strong>{hoveredStage.name}</strong>」时自动解锁
                </div>
                <div className={styles.lockInfoWay}>
                  <span className={styles.lockInfoWayLabel}>方式二</span>
                  完成「<strong>{prevStage.name}</strong>」全部关卡，每关至少 1 星，自动解锁
                </div>
              </div>
            </div>
          );
        })()}
      </section>

      {/* 当前阶段模块 */}
      <section className={styles.section}>
        <h2 className={styles.stageTitle}>{currentStage.name}</h2>
        <div className={styles.moduleList}>
          {currentStage.modules.map((module) => {
            const moduleLevels = getModuleLevels(currentStage.id, module).sort(
              (a, b) => a.order - b.order
            );
            const total = moduleLevels.length;
            const completed = getModuleCompletedCount(module);
            const status = getModuleStatus(module);
            const isExpanded = expandedModule === module;

            return (
              <div key={module}>
                <div
                  className={`${styles.moduleItem} ${isExpanded ? styles.moduleItemExpanded : ''}`}
                  onClick={() => setExpandedModule(isExpanded ? null : module)}
                >
                  <span className={`${styles.statusIcon} ${statusClass(status)}`}>
                    {statusIcon(status)}
                  </span>
                  <span className={styles.moduleName}>{module}</span>
                  <span className={styles.moduleProgress}>
                    {status === LevelStatus.NOT_STARTED ? '未开始' : `${completed}/${total}`}
                  </span>
                  <span className={`${styles.expandArrow} ${isExpanded ? styles.expandArrowOpen : ''}`}>
                    ▸
                  </span>
                </div>

                {/* 展开的关卡子列表 */}
                {isExpanded && (
                  <div className={styles.levelSubList}>
                    {moduleLevels.map((level) => {
                      const isCompleted = completedLevels.some(
                        (cl) => cl.levelId === level.id
                      );

                      return (
                        <div
                          key={level.id}
                          className={`${styles.levelItem} ${isCompleted ? styles.levelCompleted : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/level/${level.id}`);
                          }}
                        >
                          <span className={`${styles.levelStatus} ${
                            isCompleted ? styles.levelStatusDone : styles.levelStatusPending
                          }`}>
                            {isCompleted ? '✓' : (level.order <= completed + 1 ? `${level.order}` : '🔒')}
                          </span>
                          <div className={styles.levelInfo}>
                            <span className={styles.levelTitle}>
                              {level.order}. {level.title}
                            </span>
                            <span className={styles.levelMeta}>
                              {isCompleted
                                ? <StarRating stars={getStarsForLevel(level.id)} size="small" />
                                : `约 ${level.estimatedTime} 分钟`
                              }
                            </span>
                          </div>
                          <span className={styles.levelEnter}>
                            {isCompleted
                              ? (getStarsForLevel(level.id) === 0 ? '重学' : '复习')
                              : '进入'} →
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 底部导航 */}
      <nav className={styles.bottomNav}>
        <span className={styles.navItem} onClick={() => navigate('/')}>
          首页
        </span>
        <span className={`${styles.navItem} ${styles.navActive}`}>地图</span>
        <span className={styles.navItem} onClick={() => navigate('/review')}>
          复习
        </span>
        <span className={styles.navItem} onClick={() => navigate('/dashboard')}>
          掌握
        </span>
        <span className={styles.navItem} onClick={() => navigate('/achievements')}>
          成就
        </span>
      </nav>
    </div>
  );
};

export default StageMap;